export const resolveImageSrc = (src) => {
    if (!src || typeof src !== 'string') return '/images/placeholder.svg';
    const trimmed = src.trim();

    // Absolute URL: fix localhost URLs to use correct domain
    if (/^https?:\/\//i.test(trimmed)) {
        // Fix localhost URLs to use the correct domain
        if (trimmed.includes('localhost')) {
            return trimmed.replace(/https?:\/\/[^\/]+/, 'https://spoonspice.com');
        }
        return trimmed;
    }

    // If already an absolute path, return as-is
    if (trimmed.startsWith('/')) return trimmed;

    // If already prefixed with storage/, use it directly under root
    if (trimmed.startsWith('storage/')) {
        return `/${trimmed}`;
    }

    // Handle common Laravel public storage prefixes
    // e.g., "public/menus/.." or "app/public/menus/..." should resolve to "/storage/menus/..."
    if (trimmed.startsWith('public/')) {
        return `/storage/${trimmed.replace(/^public\//, '')}`;
    }
    if (trimmed.startsWith('app/public/')) {
        return `/storage/${trimmed.replace(/^app\/public\//, '')}`;
    }

    // Handle Spatie MediaLibrary URLs (e.g., "1/filename.jpg")
    if (/^\d+\//.test(trimmed)) {
        return `/storage/${trimmed}`;
    }

    // Handle old MediaLibrary paths
    if (trimmed.startsWith('media-library/')) {
        return `/storage/${trimmed}`;
    }

    // Otherwise, assume it's a path under public storage
    return `/storage/${trimmed.replace(/^\/+/, '')}`;
};

// Get the best image size for UI display
export const getImageForDisplay = (item, size = 'medium') => {
    if (!item) return '/images/placeholder.svg';
    
    // Priority 1: Spatie MediaLibrary imageVariants (new system)
    if (item.imageVariants && item.imageVariants[size]) {
        const imageUrl = item.imageVariants[size];
        if (imageUrl && imageUrl !== 'null' && imageUrl !== '') {
            const resolvedUrl = resolveImageSrc(imageUrl);
            // Check if this is a known broken image
            if (isLikelyBrokenImage(resolvedUrl)) {
                return '/images/placeholder.svg';
            }
            return resolvedUrl;
        }
    }
    
    // Priority 2: Common image fields across APIs
    const candidates = [
        // Variants object
        item.variants?.[size],
        item.variants?.medium,
        item.variants?.large,
        // Standard fields
        item.image,
        item.image_url,
        item.featured_image,
        item.url,
        item.file_path,
        // Arrays
        Array.isArray(item.images) ? item.images[0] : null,
        // Spatie media
        Array.isArray(item.media) ? (item.media[0]?.original_url || item.media[0]?.url) : null,
    ].filter(Boolean);
    
    for (const c of candidates) {
        if (c && c !== 'null' && c !== '') {
            const resolvedUrl = resolveImageSrc(c);
            // Check if this is a known broken image
            if (!isLikelyBrokenImage(resolvedUrl)) {
                return resolvedUrl;
            }
        }
    }
    
    // Final fallback - no more old system references
    return '/images/placeholder.svg';
};

// Get the best image size for detailed view (large)
export const getImageForDetail = (item) => {
    return getImageForDisplay(item, 'large');
};

// Get the best image size for UI cards (medium)
export const getImageForCard = (item) => {
    return getImageForDisplay(item, 'medium');
};

// Get a reliable placeholder image
export const getPlaceholderImage = () => '/images/placeholder.svg';

// Enhanced image error handler
export const handleImageError = (event) => {
    const target = event.currentTarget;
    if (target.src !== getPlaceholderImage()) {
        target.onerror = null; // Prevent infinite loop
        target.src = getPlaceholderImage();
        // Add a class to indicate this is a placeholder
        target.classList.add('placeholder-image');
    }
};

// Check if an image URL is likely to be broken
export const isLikelyBrokenImage = (src) => {
    if (!src || src === 'null' || src === '') return true;
    
    // Check for old MediaLibrary paths that are likely broken
    if (src.includes('/storage/media-library/')) {
        return true; // All old MediaLibrary images are likely broken
    }
    
    // Check for missing Spatie MediaLibrary files (common missing files)
    const brokenPatterns = [
        'df79dd8b-0769-45b8-bf92-0ebc5ed7de2a.png',
        '481e7b35-e3aa-4303-a951-2024e93c052a.png',
        '77766586-470e-4249-9b95-d856b5e41e0f.png',
        'healthy-homemade-meal-beef-curry-with-naan-generated-by-ai-removebg-preview.png',
        'pre-prepared-food-showcasing-ready-eat-delicious-meals-go__1_-removebg-preview.png',
        'images-(9).jpg',
        'images-(8).jpg',
        'images-(7).jpg',
        'images-(6).jpg',
        'images-(5).jpg',
        'images-(4).jpg',
        'images-(3).jpg',
        'images-(2).jpg'
    ];
    
    return brokenPatterns.some(pattern => src.includes(pattern));
};

// Get image with fallback handling
export const getImageWithFallback = (item, size = 'medium') => {
    const imageUrl = getImageForDisplay(item, size);
    
    // If the image is likely broken, return placeholder immediately
    if (isLikelyBrokenImage(imageUrl)) {
        return getPlaceholderImage();
    }
    
    return imageUrl;
};

// Get all images for an item (for galleries, carousels, etc.)
export const getAllImages = (item, size = 'medium') => {
    if (!item) return [];
    
    console.log('getAllImages called with:', { item, size });
    console.log('item.images:', item.images);
    
    const images = [];
    
    // Priority 1: images array from backend
    if (Array.isArray(item.images) && item.images.length > 0) {
        console.log('Found images array with', item.images.length, 'images');
        return item.images.map(img => {
            const url = img[size] || img.medium || img.url || img.thumb;
            return {
                id: img.id,
                url: resolveImageSrc(url),
                thumb: resolveImageSrc(img.thumb || img.url),
                medium: resolveImageSrc(img.medium || img.url),
                large: resolveImageSrc(img.large || img.url),
                name: img.name,
                size: img.size
            };
        });
    }
    
    // Priority 2: media array from Spatie MediaLibrary
    if (Array.isArray(item.media) && item.media.length > 0) {
        return item.media.map(media => ({
            id: media.id,
            url: resolveImageSrc(media.original_url || media.url),
            thumb: resolveImageSrc(media.thumb || media.url),
            medium: resolveImageSrc(media.medium || media.url),
            large: resolveImageSrc(media.large || media.url),
            name: media.name,
            size: media.size
        }));
    }
    
    // Priority 3: single image fallback
    const singleImage = getImageWithFallback(item, size);
    if (singleImage && singleImage !== getPlaceholderImage()) {
        return [{
            id: 'single',
            url: singleImage,
            thumb: singleImage,
            medium: singleImage,
            large: singleImage,
            name: item.name || 'Image',
            size: null
        }];
    }
    
    return [];
};

// Get the first image with fallback (for backward compatibility)
export const getFirstImage = (item, size = 'medium') => {
    const allImages = getAllImages(item, size);
    return allImages.length > 0 ? allImages[0].url : getPlaceholderImage();
};